import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import LocationHero from './components/LocationHero';
import LocationServices from './components/LocationServices';
import LocationFAQ from './components/LocationFAQ';
import LocationContact from './components/LocationContact';
import { getLocationBySlug } from './locationData';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://mowandsnow.ca';

export default function LocationPage() {
  const { city } = useParams<{ city: string }>();
  const navigate = useNavigate();
  const location = city ? getLocationBySlug(city) : undefined;

  useEffect(() => {
    if (!location) {
      navigate('/', { replace: true });
      return;
    }
    document.title = location.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', location.metaDescription);
    }
    window.scrollTo(0, 0);
  }, [location, navigate]);

  if (!location) return null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Locations',
        item: `${siteUrl}/locations`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: location.name,
        item: `${siteUrl}/locations/${location.slug}`,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: location.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Mow and Snow',
    description: location.metaDescription,
    telephone: '+16479837924',
    email: 'info@mowandsnow.co',
    url: `${siteUrl}/locations/${location.slug}`,
    areaServed: {
      '@type': 'City',
      name: location.name,
      addressRegion: 'ON',
      addressCountry: 'CA',
    },
    priceRange: '$$',
    openingHours: ['Mo-Fr 07:00-19:00', 'Sa-Su 08:00-17:00'],
  };

  return (
    <div className="min-h-screen bg-white">
      <title>{location.metaTitle}</title>
      <meta name="description" content={location.metaDescription} />
      <meta
        name="keywords"
        content={`lawn care ${location.name}, snow removal ${location.name}, landscaping ${location.name} Ontario, yard cleanup ${location.name}, hardscaping ${location.name}`}
      />
      <meta property="og:title" content={location.metaTitle} />
      <meta property="og:description" content={location.metaDescription} />
      <link rel="canonical" href={`${siteUrl}/locations/${location.slug}`} />
      <meta name="robots" content="index, follow" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />

      <Header />

      <main>
        <LocationHero location={location} />

        {/* Why Us Strip */}
        <section className="py-12 bg-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              {[
                { icon: 'ri-medal-line', color: 'text-yellow-400', title: 'Licensed & Insured', sub: 'Fully covered for your peace of mind' },
                { icon: 'ri-price-tag-3-line', color: 'text-green-400', title: 'Flat Monthly Rate', sub: 'No surprise charges, ever' },
                { icon: 'ri-calendar-check-line', color: 'text-cyan-400', title: 'Year-Round Service', sub: 'Summer lawn care + winter snow removal' },
                { icon: 'ri-customer-service-2-line', color: 'text-orange-400', title: 'Local & Reliable', sub: `Proud to serve ${location.name} & GTA` },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-12 h-12 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-3xl ${item.color}`}></i>
                  </div>
                  <h3 className="font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-white/70 text-sm">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <LocationServices location={location} />

        {/* Mid CTA */}
        <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Serving {location.name} homeowners all year long
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Get professional lawn care, yard cleanups, snow removal, and hardscaping — all on one simple plan.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => {
                  const el = document.getElementById('loc-contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-yellow-400 text-gray-900 font-bold rounded-full hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer"
              >
                Get Your Free Quote
              </button>
              <a
                href="tel:+16479837924"
                className="px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer inline-flex items-center"
              >
                <i className="ri-phone-line mr-2"></i>
                (647) 983-7924
              </a>
            </div>
          </div>
        </section>

        <LocationContact location={location} />
        <LocationFAQ location={location} />
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
